import { navigate } from 'gatsby'
import { MouseEvent as ReactMouseEvent, useCallback, useState } from 'react'
import { useAppState } from '../appState'
import { ROUTES } from '../constants'

export const DIGITAL_MARKETER_SEGMENT = 'digital_marketer'

/**
 * Cashback is disabled for the Digital Marketer share-link segment (product decision,
 * CH24SCRUM-676) — there is no separate per-user "cashback disabled" flag to check.
 */
export const isDigitalMarketerCashbackDisabled = (userProfile: any): boolean =>
  userProfile?.shareLinkSegment === DIGITAL_MARKETER_SEGMENT

/**
 * Shared shop-now interceptor. Every "shop now" call site — anchor or click handler —
 * gates its redirect through this hook instead of navigating directly, and renders
 * `<DigitalMarketerShopModal>` with the state it returns. One rule, one place: nothing
 * downstream re-implements the segment check or the redirect-to-share-link-tool copy.
 *
 * Two entry points cover every call-site shape in this codebase:
 * - `interceptAnchorClick(originalUrl, onProceed?)` — for `<a>` tags; returns an onClick
 *   handler that `preventDefault()`s and opens the modal when gated, otherwise runs
 *   `onProceed` (e.g. click-stream tracking) and lets the click through normally.
 * - `interceptShopNow(proceed, originalUrl?)` — for handler-style shop actions
 *   (`window.location.href = ...`, `window.open(...)`); runs `proceed` unless gated.
 *
 * `originalUrl` is remembered so `goToShareLinkTool()` (called from the modal's CTA) can
 * prefill the share-link tool with the plain retailer URL, not the affiliate-tracking link.
 *
 * `modalProps` is spread directly onto `<DigitalMarketerShopModal {...modalProps} />` —
 * no call site wires up `open`/`onClose`/`onCreateShareLink` by hand.
 */
export const useDigitalMarketerShopGate = () => {
  const [appState] = useAppState() as any
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [pendingOriginalUrl, setPendingOriginalUrl] = useState<
    string | undefined
  >(undefined)

  const isGated = isDigitalMarketerCashbackDisabled(appState.userProfile)

  const openModal = useCallback((originalUrl?: string) => {
    setPendingOriginalUrl(originalUrl)
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => setIsModalOpen(false), [])

  const interceptShopNow = useCallback(
    (proceed: () => void, originalUrl?: string) => {
      if (isGated) {
        openModal(originalUrl)
        return
      }
      proceed()
    },
    [isGated, openModal]
  )

  const interceptAnchorClick = useCallback(
    (
      originalUrl?: string,
      onProceed?: (event: ReactMouseEvent<HTMLAnchorElement>) => void
    ) => (event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (isGated) {
        event.preventDefault()
        openModal(originalUrl)
        return
      }
      onProceed?.(event)
    },
    [isGated, openModal]
  )

  const goToShareLinkTool = useCallback(() => {
    setIsModalOpen(false)
    navigate(
      pendingOriginalUrl
        ? `${ROUTES.createShareLink}?originalLink=${encodeURIComponent(
            pendingOriginalUrl
          )}`
        : ROUTES.createShareLink
    )
  }, [pendingOriginalUrl])

  return {
    isGated,
    interceptShopNow,
    interceptAnchorClick,
    modalProps: {
      open: isModalOpen,
      onClose: closeModal,
      onCreateShareLink: goToShareLinkTool,
    },
  }
}
